/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { nodesApi, searchApi, sitesApi } from "../AlfrescoApi.js";
import { newPristyError } from "./ErrorService.js";
import QuickLRU from "quick-lru";

/**
 * Service class for managing and interacting with workspaces.
 */
class WorkspaceService {
  constructor() {
    this.lruWorkspaces = new QuickLRU({
      maxSize: 100,
      maxAge: 30000, // 30 Sec
    });
  }
  getWorkspaces() {
    const cacheKey = "workspaces";
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    const opts = {
      skipCount: 0,
      maxItems: 1000,
    };

    return sitesApi
      .listSites(opts)
      .then((data) => {
        const workspaces = data.list.entries.map((data) => data.entry);
        this.lruWorkspaces.set(cacheKey, workspaces);
        return workspaces;
      })
      .catch((err) => {
        throw newPristyError("getWorkspaces", err);
      });
  }

  searchAllWorkspaces(extraQuery = "") {
    const cacheKey = `searchAllWorkspaces_${extraQuery}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    const query = {
      query: {
        //language: "afts",
        query: "TYPE: 'st:site' " + extraQuery,
      },
      include: ["allowableOperations", "properties", "aspectNames"],
      paging: {
        maxItems: 1000,
        skipCount: 0,
      },
      localization: { locales: ["fr_FR"] },
    };

    return searchApi
      .search(query)
      .then((data) => {
        const workspaces = data.list.entries.map((data) => {
          let obj = data.entry;
          // add some basic properties to match sitesApi.listSites output
          obj.title = data.entry.properties["cm:title"];
          obj.visibility = data.entry.properties["st:siteVisibility"];
          obj.typeEspace = data.entry.properties["pm:typeEspace"];
          return obj;
        });
        this.lruWorkspaces.set(cacheKey, workspaces);
        return workspaces;
      })
      .catch((err) => {
        throw newPristyError("searchAllWorkspaces", err);
      });
  }

  /**
   * Call sitesApi.getSite to get Site
   * @param id siteid
   * @returns {Promise<Site>}
   */
  getWorkspace(id) {
    const cacheKey = `workspace_${id}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    const opts = {};

    return sitesApi
      .getSite(id, opts)
      .then((data) => {
        this.lruWorkspaces.set(cacheKey, data.entry);
        return data.entry;
      })
      .catch((err) => {
        throw newPristyError("getWorkspace", err);
      });
  }

  getWorkspaceMemberships(id) {
    const cacheKey = `workspaceMemberships_${id}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    const opts = {};

    return sitesApi
      .listSiteMemberships(id, opts)
      .then((data) => {
        const memberships = data.list.entries.map((data) => data.entry);
        this.lruWorkspaces.set(cacheKey, memberships);
        return memberships;
      })
      .catch((err) => {
        throw newPristyError("getWorkspaceMemberships", err);
      });
  }

  getWorkspaceMembershipsWithOpts(id, opts) {
    const cacheKey = `workspaceMembershipsWithOpts_${id}_${JSON.stringify(opts)}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    return sitesApi
      .listSiteMemberships(id, opts)
      .then((data) => {
        this.lruWorkspaces.set(cacheKey, data);
        return data;
      })
      .catch((err) => {
        throw newPristyError("getWorkspaceMemberships", err);
      });
  }

  getWorkspaceGroupsWithOpts(id, opts) {
    const cacheKey = `workspaceGroupsWithOpts_${id}_${JSON.stringify(opts)}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    return sitesApi
      .listSiteGroups(id, opts)
      .then((data) => {
        const groups = data.list.entries.map((data) => data.entry);
        this.lruWorkspaces.set(cacheKey, groups);
        return groups;
      })
      .catch((err) => {
        throw newPristyError("getWorkspaceGroups", err);
      });
  }

  getWorkspaceGroups(id) {
    const cacheKey = `workspaceGroups_${id}`;
    if (this.lruWorkspaces.has(cacheKey)) {
      return Promise.resolve(this.lruWorkspaces.get(cacheKey));
    }

    const opts = {};

    return sitesApi
      .listSiteGroups(id, opts)
      .then((data) => {
        const groups = data.list.entries.map((data) => data.entry);
        this.lruWorkspaces.set(cacheKey, groups);
        return groups;
      })
      .catch((err) => {
        throw newPristyError("getWorkspaceGroups", err);
      });
  }

  createWorkspace(id, title, descr, visibility) {
    const opts = {
      id: id,
      title: title,
      description: descr,
      visibility: visibility,
    };

    return sitesApi.createSite(opts).catch((err) => {
      throw newPristyError("createWorkspace", err);
    });
  }

  requestJoinWorkspace(personId, siteId) {
    const siteMemberShipRequestBody = {
      id: siteId,
    };
    const opts = {};

    return sitesApi
      .createSiteMembershipRequestForPerson(
        personId,
        siteMemberShipRequestBody,
        opts,
      )
      .catch((err) => {
        throw newPristyError("requestJoinWorkspace", err);
      });
  }

  /**
   * Invalidate cache entries for workspace members
   * @private
   */
  _invalidateMembersCache(siteId) {
    // Invalidate all cache entries related to this workspace's members
    const keysToDelete = [];
    for (const key of this.lruWorkspaces.keys()) {
      if (
        key.startsWith(`workspaceMemberships_${siteId}`) ||
        key.startsWith(`workspaceMembershipsWithOpts_${siteId}`) ||
        key.startsWith(`workspaceGroups_${siteId}`) ||
        key.startsWith(`workspaceGroupsWithOpts_${siteId}`)
      ) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => this.lruWorkspaces.delete(key));
  }

  addWorkspaceUser(siteId, userId, role) {
    const memberShipBody = {
      role: role,
      id: userId,
    };
    const opts = {};

    return sitesApi
      .createSiteMembership(siteId, memberShipBody, opts)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("addWorkspaceUser", err);
      });
  }

  addWorkspaceGroup(siteId, groupId, role) {
    const groupBody = {
      role: role,
      id: groupId,
    };
    const opts = {};

    return sitesApi
      .createSiteGroupMembership(siteId, groupBody, opts)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("addWorkspaceGroup", err);
      });
  }

  updateWorkspaceUserRole(siteId, userId, newRole) {
    const siteMembershipBody = {
      role: newRole,
    };
    const opts = {};

    return sitesApi
      .updateSiteMembership(siteId, userId, siteMembershipBody, opts)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("updateWorkspaceUserRole", err);
      });
  }

  updateWorkspaceGroupRole(siteId, groupId, newRole) {
    const siteGroupBody = {
      role: newRole,
    };
    const opts = {};

    return sitesApi
      .updateSiteGroupMembership(siteId, groupId, siteGroupBody, opts)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("updateWorkspaceGroupRole", err);
      });
  }

  deleteWorkspaceUser(siteId, userId) {
    return sitesApi
      .deleteSiteMembership(siteId, userId)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("deleteWorkspaceUser", err);
      });
  }

  deleteWorkspaceGroup(siteId, groupId) {
    return sitesApi
      .deleteSiteGroupMembership(siteId, groupId)
      .then((result) => {
        this._invalidateMembersCache(siteId);
        return result;
      })
      .catch((err) => {
        throw newPristyError("deleteWorkspaceGroup", err);
      });
  }

  updateActeWorkspaceType(id, typeEspace) {
    const opts = {
      properties: {
        "pm:typeEspace": typeEspace,
      },
      aspectNames: [
        "cm:tagscope",
        "pm:specializeEspace",
        "cm:titled",
        "cm:auditable",
        "am:isPublic",
      ],
    };
    return nodesApi.updateNode(id, opts).catch((err) => {
      throw newPristyError("updateWorkspaceType", err);
    });
  }

  updateWorkspaceType(id, typeEspace) {
    const opts = {
      properties: {
        "pm:typeEspace": typeEspace,
      },
    };

    return nodesApi.updateNode(id, opts).catch((err) => {
      throw newPristyError("updateWorkspaceType", err);
    });
  }

  /**
   * Search users by term using AFTS query on userName, firstName, lastName, email.
   * @param {string} term - Search term (minimum 2 characters recommended)
   * @param {number} maxItems - Maximum number of results (default 25)
   * @returns {Promise<Array>} Normalized user list
   */
  async searchUsers(term, maxItems = 25) {
    const escapedTerm = term.trim();
    const query = {
      query: {
        language: "afts",
        query:
          `cm:userName:"*${escapedTerm}*" OR ` +
          `cm:firstName:"*${escapedTerm}*" OR ` +
          `cm:lastName:"*${escapedTerm}*" OR ` +
          `cm:email:"*${escapedTerm}*"`,
      },
      filterQueries: [{ query: 'TYPE:"cm:person"' }],
      // Force en_US: AFTS wildcard search on cm:person d:text fields returns
      // nothing under a fr_FR locale (the browser Accept-Language). See
      // PeopleService.searchUser for the full explanation.
      localization: { locales: ["en_US"] },
      include: ["aspectNames", "properties"],
      paging: {
        maxItems: maxItems,
        skipCount: 0,
      },
    };

    return searchApi
      .search(query)
      .then((response) => {
        return response.list.entries.map((entry) => {
          const props = entry.entry.properties;
          const firstName = props["cm:firstName"] || "";
          const lastName = props["cm:lastName"] || "";
          const email = props["cm:email"] || "";
          const userName = props["cm:userName"] || entry.entry.name;
          return {
            id: userName,
            firstname: firstName,
            lastname: lastName,
            email: email,
            label: `${firstName} ${lastName} (${userName} - ${email})`.trim(),
            group: false,
          };
        });
      })
      .catch((err) => {
        throw newPristyError("searchUsers", err);
      });
  }

  /**
   * Search groups by term using AFTS query.
   * Natural search covers cm:authorityName (group id), cm:authorityDisplayName
   * and cm:description (from the cm:titled aspect, optional).
   * When the term starts with "GROUP_", searches only on cm:authorityName
   * so that users explicitly looking up a group by its id are not polluted
   * by display-name or description matches.
   * Filters out system groups (site_, SITE_, ALFRESCO_, EMAIL_).
   * @param {string} term - Search term (minimum 2 characters recommended)
   * @param {number} maxItems - Maximum number of results (default 25)
   * @returns {Promise<Array>} Normalized group list
   */
  async searchGroups(term, maxItems = 25) {
    const escapedTerm = term.trim();
    const isGroupIdSearch = escapedTerm.startsWith("GROUP_");
    const matcher = isGroupIdSearch
      ? `@cm\\:authorityName:"${escapedTerm}*"`
      : `(@cm\\:authorityName:"*${escapedTerm}*" OR ` +
        `@cm\\:authorityDisplayName:"*${escapedTerm}*" OR ` +
        `@cm\\:description:"*${escapedTerm}*")`;
    const query = {
      query: {
        language: "afts",
        query: matcher,
      },
      filterQueries: [{ query: 'TYPE:"cm:authorityContainer"' }],
      // Force en_US: AFTS wildcard search on the d:text fields
      // cm:authorityDisplayName / cm:description returns nothing under a fr_FR
      // locale. cm:authorityName (identifier) is unaffected. See
      // PeopleService.searchUser for the full explanation.
      localization: { locales: ["en_US"] },
      include: ["properties"],
      paging: {
        maxItems: maxItems,
        skipCount: 0,
      },
    };

    return searchApi
      .search(query)
      .then((response) => {
        const excludedPrefixes = ["site_", "SITE_", "ALFRESCO_", "EMAIL_"];
        return response.list.entries
          .map((entry) => {
            const props = entry.entry.properties;
            const authorityName = props["cm:authorityName"];
            return {
              id: authorityName,
              label: props["cm:authorityDisplayName"] || authorityName,
              group: true,
            };
          })
          .filter(
            (group) =>
              !excludedPrefixes.some((prefix) =>
                group.label.startsWith(prefix),
              ),
          );
      })
      .catch((err) => {
        throw newPristyError("searchGroups", err);
      });
  }

  deleteWorkspace(siteId, permanent = false) {
    const opts = { permanent };

    return sitesApi.deleteSite(siteId, opts).catch((err) => {
      throw newPristyError("deleteWorkspace", err);
    });
  }

  updateWorkspace(id, opts) {
    return sitesApi.updateSite(id, opts).catch((err) => {
      throw newPristyError("updateWorkspace", err);
    });
  }
}

export default new WorkspaceService();
